/**
 * ⭐ PEDIDOS DE PESQUISA — o lado do Hub. 17/09/2026 (PRD §43.13).
 *
 * Desenho aprovado em `docs/sistema-visual-2026/pedido-17-09/` (h2 a h6).
 *
 * - **Link de pedido:** um por cliente, não vence. Gerar de novo cancela o
 *   anterior na hora. ⛔ O Hub não manda nada: quem manda é a pessoa.
 * - **Pedidos de pesquisa** (na ficha): o que chegou, e o link do cliente.
 * - **Conferir o pedido:** tudo o que o cliente escreveu, com os avisos que são
 *   só da KAIRÓS (teto do nível, documento que falta). *Montar a proposta* abre
 *   a Nova proposta de sempre, já preenchida — o honorário é da KAIRÓS.
 * - **Trazer do pedido** (no caso): investigados e marcos da dívida, **só no
 *   vazio**. ⛔ O que o cliente sabe vai para a observação do CASO, nunca para a
 *   do investigado — aquela sai impressa no relatório.
 *
 * ⛔ Tudo aqui é do master: a tela nem aparece para o colaborador, e o servidor
 * recusa as rotas (`exigirMaster`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ErroDaApi,
  QUALIFICACOES,
  apiFormulario,
  type DividaDoPedido,
  type OpcoesParaTrazer,
  type PedidoDePesquisa,
} from '../lib/api.ts';
import { paraTela } from '../lib/datas.ts';
import { pedidoParaAbrir } from '../lib/pedido-a-abrir.ts';
import { paraReais } from '../lib/dinheiro.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { NIVEIS, formatarProcesso, mascararDocumento } from '../lib/rotulos.ts';
import { avisar, avisarErro } from './Avisos.tsx';
import { AvisoSimples, copiar } from './CadastrosPeloLink.tsx';
import { Confirmar } from './Confirmar.tsx';
import { Icone } from './Icone.tsx';
import { QuadroDeDocumentos } from './DocumentosDoPedido.tsx';
import '../estilos/cadastro-link.css';
import '../estilos/pedido-link.css';

const FUSO = 'America/Sao_Paulo';
const dia = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit' }).format(new Date(iso));
const diaAno = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));
const hora = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

export function Selos({ niveis }: { niveis: string[] }) {
  return (
    <span className="ped-selos">
      {niveis.map((n, i) => (
        <span key={i} className={`selo-nivel selo-${n}`}>
          {NIVEIS[n as keyof typeof NIVEIS]?.longo ?? n}
        </span>
      ))}
    </span>
  );
}

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** O que a Nova proposta recebe do pedido. */
export type PropostaDoPedido = {
  id: string;
  recebidoEm: string;
  quem: string;
  pesquisas: PedidoDePesquisa['proposta'];
};

// ═══════════════════════════════════════════════════════════════════════════
// O painel da ficha
// ═══════════════════════════════════════════════════════════════════════════

export function PedidosDoCliente({
  clienteId,
  clienteNome,
  versao,
  gerandoLink,
  aoFecharLink,
  aoMontarProposta,
  aoMudar,
  aoExpirarSessao,
}: {
  clienteId: string;
  clienteNome: string;
  /** Muda quando a ficha recarrega (uma proposta montada tira o pedido da lista). */
  versao: number;
  /** O botão "Link de pedido" do topo da ficha. */
  gerandoLink: boolean;
  aoFecharLink: () => void;
  aoMontarProposta: (p: PropostaDoPedido) => void;
  /** Há pedido (a conferir ou montado)? Decide o "Trazer do pedido" nos casos. */
  aoMudar: (temPedido: boolean) => void;
  aoExpirarSessao: () => void;
}) {
  const [dados, setDados] = useState<Awaited<ReturnType<typeof apiFormulario.pedidosDoCliente>> | null>(null);
  const [conferindo, setConferindo] = useState<string | null>(null);
  const [mostrando, setMostrando] = useState<{ endereco: string; novo: boolean } | null>(null);
  const [trocando, setTrocando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await apiFormulario.pedidosDoCliente(clienteId);
      setDados(r);
      aoMudar(r.aConferir.length + r.montados > 0);
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      /* ⚠️ Falha aqui não derruba a ficha: o painel some, e o resto segue. */
      setDados(null);
    }
  }, [clienteId, aoMudar, aoExpirarSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar, versao]);

  // A aba Clientes pediu para abrir um pedido nesta ficha.
  useEffect(() => {
    const id = pedidoParaAbrir(clienteId);
    if (id) setConferindo(id);
  }, [clienteId]);

  const gerar = useCallback(async () => {
    try {
      const r = await apiFormulario.gerarLinkDePedido(clienteId);
      setMostrando({ endereco: r.endereco, novo: true });
      void carregar();
    } catch (e) {
      avisarErro(e);
    }
  }, [clienteId, carregar]);

  const copiarDeNovo = useCallback(async () => {
    if (!dados?.link) return;
    try {
      const r = await apiFormulario.endereco(dados.link.id);
      setMostrando({ endereco: r.endereco, novo: false });
    } catch (e) {
      avisarErro(e);
      void carregar();
    }
  }, [dados, carregar]);

  // O botão do topo: com link, mostra o que existe; sem link, pergunta antes de gerar.
  useEffect(() => {
    if (!gerandoLink || !dados) return;
    if (dados.link) void copiarDeNovo();
    else setTrocando(true);
    aoFecharLink();
  }, [gerandoLink, dados, copiarDeNovo, aoFecharLink]);

  const temAlgo = !!dados && (dados.link !== null || dados.aConferir.length > 0);

  return (
    <>
      {temAlgo && dados && (
        <section className="s-folha cad-painel ped-painel-ficha" aria-label="Pedidos de pesquisa">
          <div className="cad-painel-topo">
            <div>
              <span className="s-sobrelinha">Pedidos de pesquisa</span>
              <h2>O que este cliente pediu pelo link</h2>
              <p>O pedido não vira proposta sozinho: você confere e monta.</p>
            </div>
          </div>

          {dados.aConferir.length > 0 && (
            <div className="cad-grupo">
              <div className="cad-grupo-titulo">
                <span className="s-micro">Chegou · a conferir</span>
                <span className="cad-conta">{dados.aConferir.length}</span>
              </div>
              {dados.aConferir.map((p) => (
                <div key={p.id} className="cad-linha chegou">
                  <span className="cad-ponto" />
                  <div>
                    <p className="cad-nome">
                      <Selos niveis={p.niveis} />
                      {p.primeiro ? `${p.primeiro}${p.investigados > 1 ? ` e mais ${p.investigados - 1}` : ''}` : 'Pedido que não passou na conferência'}
                    </p>
                    <p className="cad-meta">
                      <span>
                        {plural(p.pesquisas, 'pesquisa', 'pesquisas')} · {plural(p.investigados, 'investigado', 'investigados')}
                        {p.documentos > 0 && <> · {plural(p.documentos, 'documento', 'documentos')}</>}
                      </span>
                      {p.quem && <span>pedido por {p.quem}</span>}
                    </p>
                  </div>
                  <span className="cad-quando">
                    chegou <b>{dia(p.recebidoEm)} às {hora(p.recebidoEm)}</b>
                  </span>
                  <span className="cad-acoes">
                    <button type="button" className="botao-primario s-botao s-botao-p" onClick={() => setConferindo(p.id)}>
                      Conferir o pedido <Icone nome="avancar" tamanho={14} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="cad-grupo">
            <div className="cad-grupo-titulo">
              <span className="s-micro">O link deste cliente</span>
            </div>
            {dados.link ? (
              <div className="cad-linha aguarda">
                <span className="cad-ponto" />
                <div>
                  <p className="cad-nome">Link de pedido ativo</p>
                  <p className="cad-meta">
                    <span>não expira · um por cliente · {plural(dados.link.recebidos, 'pedido recebido', 'pedidos recebidos')}</span>
                  </p>
                </div>
                <span className="cad-quando">
                  gerado <b>{dia(dados.link.criadoEm)}</b>
                </span>
                <span className="cad-acoes">
                  <button type="button" className="botao-texto s-botao s-botao-p" onClick={() => void copiarDeNovo()}>
                    <Icone nome="copiar" tamanho={14} /> Copiar de novo
                  </button>
                  <button type="button" className="botao-texto botao-perigo s-botao s-botao-p" onClick={() => setTrocando(true)}>
                    Cancelar e gerar outro
                  </button>
                </span>
              </div>
            ) : (
              <p className="cad-nada">Nenhum link ativo. Use “Link de pedido” no alto da ficha.</p>
            )}
          </div>
        </section>
      )}

      {trocando && (
        <Confirmar
          titulo={dados?.link ? 'Cancelar e gerar outro link?' : 'Gerar o link de pedido?'}
          descricao={
            dados?.link
              ? 'O link atual deixa de abrir na hora — quem o guardou passa a ver “indisponível”. O novo precisa ser enviado ao cliente.'
              : `${clienteNome} passa a ter um link para pedir pesquisas. Ele não expira, e cada envio chega aqui como um pedido.`
          }
          rotuloAcao={dados?.link ? 'Cancelar e gerar outro' : 'Gerar link'}
          aoFechar={() => setTrocando(false)}
          aoConfirmar={async () => {
            setTrocando(false);
            await gerar();
          }}
        />
      )}

      {mostrando && <DialogoDoLinkDePedido clienteNome={clienteNome} endereco={mostrando.endereco} novo={mostrando.novo} aoFechar={() => setMostrando(null)} />}

      {conferindo && (
        <ConferirPedido
          pedidoId={conferindo}
          aoFechar={() => setConferindo(null)}
          aoMontar={(p) => {
            setConferindo(null);
            aoMontarProposta(p);
          }}
          aoDescartado={() => {
            setConferindo(null);
            void carregar();
          }}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// O link
// ═══════════════════════════════════════════════════════════════════════════

const REGRAS = [
  <>
    <b>Não expira</b> e continua valendo depois de cada envio: o cliente usa o mesmo link para todo pedido.
  </>,
  <>
    <b>Um link por cliente.</b> <b>Cancelar e gerar outro</b> desliga o anterior na hora.
  </>,
  <>A página não mostra o nome do cliente: link encaminhado por engano não revela quem contratou.</>,
  <>Cada envio chega como um pedido separado, e nada vira proposta sem você montar.</>,
];

const mensagemPronta = (endereco: string) =>
  'Olá! Este é o link para pedir pesquisas à KAIRÓS. Ele é exclusivo da sua empresa e não expira: guarde-o e use sempre que precisar de uma nova pesquisa.\n' +
  `${endereco}\n` +
  '— KAIRÓS Asset Intelligence';

function DialogoDoLinkDePedido({ clienteNome, endereco, novo, aoFechar }: { clienteNome: string; endereco: string; novo: boolean; aoFechar: () => void }) {
  usarDialogo(aoFechar);
  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="dialogo cad-dialogo" role="dialog" aria-modal="true" aria-label="Link de pedido de pesquisa">
        <h2 className="titulo-secao">Link de pedido de pesquisa</h2>
        <div className="cad-dialogo-corpo imv">
          {novo && <p className="cad-sub">Link novo gerado. Envie ao cliente pelo canal de costume.</p>}
          <p className="cad-apelido">
            <span className="imv-rotulo">Cliente</span> {clienteNome}
          </p>
          <div>
            <span className="imv-rotulo">Link do cliente</span>
            <div className="cad-link">
              <code>{endereco}</code>
              <button type="button" className="botao-primario s-botao s-botao-p" onClick={() => copiar(endereco, 'Link')}>
                <Icone nome="copiar" tamanho={14} /> Copiar link
              </button>
            </div>
          </div>
          <div className="cad-mensagem">
            <span className="imv-rotulo">Mensagem pronta para mandar</span>
            <p>{mensagemPronta(endereco)}</p>
            <button type="button" className="botao-secundario s-botao s-botao-p" onClick={() => copiar(mensagemPronta(endereco), 'Mensagem')}>
              <Icone nome="copiar" tamanho={14} /> Copiar mensagem
            </button>
          </div>
          <ul className="cad-regras">
            {REGRAS.map((r, i) => (
              <li key={i}>
                <Icone nome="check" tamanho={14} />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="dialogo-acoes">
          <button type="button" className="botao-primario" onClick={aoFechar}>
            Pronto
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Conferir o pedido
// ═══════════════════════════════════════════════════════════════════════════

function linhasDaDivida(d: DividaDoPedido): Array<[string, string, boolean]> {
  return [
    ['Valor', d.valor ? paraReais(d.valor) : '', true],
    ['Contrato ou título', d.contratoEm ? paraTela(d.contratoEm) : '', true],
    ['Inadimplência', d.inadimplenciaEm ? paraTela(d.inadimplenciaEm) : '', true],
    [
      'Processo',
      d.temProcesso ? [d.processo ? formatarProcesso(d.processo) : 'número não informado', d.ajuizadoEm ? `ajuizado em ${paraTela(d.ajuizadoEm)}` : ''].filter(Boolean).join(' · ') : 'ainda não há',
      false,
    ],
  ];
}

function BlocoDl({ titulo, linhas }: { titulo: string; linhas: Array<[string, string, boolean]> }) {
  return (
    <section className="cad-bloco">
      <header>
        <h6>{titulo}</h6>
      </header>
      <dl>
        {linhas.map(([r, v, mono]) => (
          <div key={r}>
            <dt>{r}</dt>
            <dd className={v ? (mono ? 'dado' : 'texto') : 'falta'}>{v || 'não informado'}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function ConferirPedido({ pedidoId, aoFechar, aoMontar, aoDescartado }: { pedidoId: string; aoFechar: () => void; aoMontar: (p: PropostaDoPedido) => void; aoDescartado: () => void }) {
  const [p, setP] = useState<PedidoDePesquisa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [descartando, setDescartando] = useState(false);
  usarDialogo(aoFechar);

  useEffect(() => {
    apiFormulario
      .pedido(pedidoId)
      .then(setP)
      .catch((e) => setErro(e instanceof Error ? e.message : 'Não consegui abrir o pedido.'));
  }, [pedidoId]);

  if (erro) return <AvisoSimples titulo="Pedido de pesquisa" texto={erro} aoFechar={aoFechar} />;
  if (!p) {
    return (
      <div className="cortina cad-cortina">
        <div className="dialogo">Abrindo o pedido…</div>
      </div>
    );
  }
  const d = p.pedido.dados;
  // O pedido cuja proposta foi apagada volta a poder ser montado ou descartado.
  const resolvido = !(p.pedido.situacao === 'a_conferir' || (p.pedido.situacao === 'proposta_montada' && p.pedido.contratoId === null));
  const varias = d.pesquisas.length > 1;
  const mesma = !varias || d.mesmaDivida;
  const avisosDaPesquisa = (i: number) => p.avisos.filter((a) => a.startsWith(`Pesquisa ${i + 1}:`)).map((a) => a.replace(/^Pesquisa \d+: /, ''));

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="imv cad-conferir" role="dialog" aria-modal="true" aria-label="Conferir o pedido">
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">Pedido de pesquisa · {p.pedido.cliente.nome}</span>
              <h3 className="cad-titulo">Conferir o pedido</h3>
              <p className="cad-sub">Leia o que chegou e monte a proposta. O honorário e as condições são seus: o cliente não viu nenhum.</p>
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={aoFechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>
          <div className="cad-chegada">
            <Icone nome="caixa" tamanho={18} />
            <span>
              <b>
                Chegou pelo link em {diaAno(p.pedido.recebidoEm)} às {hora(p.pedido.recebidoEm)}
              </b>{' '}
              · pedido por {d.quem.nome} ({[d.quem.email, d.quem.telefone].filter(Boolean).join(' · ')}) · o cliente marcou a declaração de privacidade e de finalidade lícita.
            </span>
          </div>
          <div className="imv-corpo ped-corpo-conferir">
            <div className="imv-conteudo">
              {d.pesquisas.map((q, i) => (
                <section key={i} className="cad-bloco ped-bloco-pesquisa">
                  <header>
                    <h6>
                      Pesquisa {i + 1} <Selos niveis={[q.nivel]} />
                    </h6>
                  </header>
                  <div className="cad-tabela">
                    <div className="cad-tl cad-tl-cab">
                      <span>Investigado</span>
                      <span>Papel na dívida</span>
                      <span>O que o cliente sabe</span>
                    </div>
                    {q.investigados.map((x, j) => {
                      const doc = x.documento ? mascararDocumento(x.documento, x.tipo) : '';
                      const rec = x.tipo === 'pj' && x.documento ? p.receita[x.documento] : undefined;
                      const confere = rec && rec.razaoSocial.toUpperCase() === x.nome.toUpperCase();
                      return (
                        <div key={j} className="cad-tl">
                          <span>
                            <b>{x.nome.toUpperCase()}</b>
                            <br />
                            <span className="dado">{doc || 'sem documento'}</span>
                            {rec && (
                              <span className={`ped-ok${confere ? '' : ' difere'}`} title={confere ? undefined : `No cadastro da Receita: ${rec.razaoSocial}`}>
                                <Icone nome={confere ? 'check' : 'atencao'} tamanho={12} /> {confere ? 'Receita' : `Receita: ${rec.razaoSocial}`}
                              </span>
                            )}
                          </span>
                          <span>{QUALIFICACOES[x.papel]}</span>
                          <span className="ped-sabe">{x.sabe || '—'}</span>
                        </div>
                      );
                    })}
                  </div>
                  {avisosDaPesquisa(i).map((a) => (
                    <p key={a} className="ped-aviso-kairos">
                      <Icone nome="atencao" tamanho={16} />
                      <span>{a}</span>
                    </p>
                  ))}
                </section>
              ))}
              <div className="cad-resumo">
                {mesma ? (
                  <BlocoDl titulo={varias ? `A dívida · ${d.pesquisas.length === 2 ? 'as duas pesquisas' : `as ${d.pesquisas.length} pesquisas`}` : 'A dívida'} linhas={linhasDaDivida(d.pesquisas[0]!.divida)} />
                ) : (
                  d.pesquisas.map((q, i) => <BlocoDl key={i} titulo={`A dívida · pesquisa ${i + 1}`} linhas={linhasDaDivida(q.divida)} />)
                )}
                <BlocoDl titulo="Algo mais" linhas={[['O cliente escreveu', d.observacao, false]]} />
              </div>
              {/* ⭐ Os documentos que o cliente anexou (18/09/2026, PRD §43.15). */}
              <QuadroDeDocumentos pedidoId={p.pedido.id} iniciais={p.documentos ?? []} />
            </div>
            <aside className="imv-ajuda">
              <span className="imv-rot ouro">O que acontece agora</span>
              <p>
                <b>Montar a proposta</b> abre a Nova proposta com {varias ? `as ${d.pesquisas.length} pesquisas` : 'a pesquisa'} e os investigados de cada uma. Você completa honorário e condições, como sempre.
              </p>
              <p>
                A dívida e o que o cliente sabe <b>ficam guardados no pedido</b>. Quando você lançar o caso, o botão <b>Trazer do pedido</b> os copia.
              </p>
              {(p.documentos ?? []).length > 0 && (
                <p>
                  Os <b>documentos</b> são conferidos sozinhos antes de abrir. Quando o caso nascer da proposta, eles aparecem nele em <b>Recebido do cliente</b>.
                </p>
              )}
              <p>
                Pedido que não vai adiante? <b>Descarte</b>: ele sai da lista.
              </p>
            </aside>
          </div>
          <footer className="imv-ficha-pe">
            {!resolvido && (
              <button type="button" className="botao-texto botao-perigo s-botao s-botao-p" onClick={() => setDescartando(true)}>
                Descartar este pedido
              </button>
            )}
            <span className="imv-esp" />
            {resolvido ? (
              <span className="cad-sub">Este pedido já foi resolvido.</span>
            ) : (
              <button
                type="button"
                className="imv-bt primario"
                onClick={() => aoMontar({ id: p.pedido.id, recebidoEm: p.pedido.recebidoEm, quem: d.quem.nome, pesquisas: p.proposta })}
              >
                Montar a proposta <Icone nome="avancar" tamanho={14} />
              </button>
            )}
          </footer>
        </section>
      </div>

      {descartando && (
        <Confirmar
          titulo="Descartar este pedido?"
          descricao={
            'Ele sai da lista e nada muda na carteira. O cliente não é avisado, e o link dele continua valendo.' +
            /* ⭐ Descartar APAGA os documentos (decisão dele, 18/09/2026) — e a janelinha diz quantos. */
            ((p.documentos ?? []).length
              ? ` ${(p.documentos ?? []).length === 1 ? 'O documento que o cliente anexou é apagado' : `Os ${(p.documentos ?? []).length} documentos que o cliente anexou são apagados`} — o arquivo e as páginas — e não há como recuperar.`
              : '')
          }
          rotuloAcao="Descartar"
          aoFechar={() => setDescartando(false)}
          aoConfirmar={async () => {
            try {
              await apiFormulario.descartar(p.pedido.id);
              avisar('Pedido descartado.');
              aoDescartado();
            } catch (e) {
              avisarErro(e);
              setDescartando(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Trazer do pedido — só o vazio do caso
// ═══════════════════════════════════════════════════════════════════════════

export function TrazerDoPedido({ casoId, casoCodigo, aoFechar, aoTrazido }: { casoId: string; casoCodigo: string; aoFechar: () => void; aoTrazido: () => void }) {
  const [op, setOp] = useState<OpcoesParaTrazer | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [escolha, setEscolha] = useState<{ pedidoId: string; pesquisa: number } | null>(null);
  const [marcados, setMarcados] = useState<Set<number>>(new Set());
  const [divida, setDivida] = useState(true);
  const [notas, setNotas] = useState(true);
  const [trazendo, setTrazendo] = useState(false);
  usarDialogo(aoFechar);

  useEffect(() => {
    apiFormulario
      .paraTrazer(casoId)
      .then((r) => {
        setOp(r);
        setEscolha(r.sugestao);
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Não consegui abrir os pedidos.'));
  }, [casoId]);

  const pedido = op?.pedidos.find((x) => x.id === escolha?.pedidoId) ?? null;
  const pesquisa = pedido?.pesquisas[escolha?.pesquisa ?? 0] ?? null;

  // Trocou a pesquisa: marca quem ainda não está no caso.
  useEffect(() => {
    setMarcados(new Set(pesquisa?.investigados.filter((x) => !x.jaNoCaso).map((x) => x.indice) ?? []));
    setDivida(true);
    setNotas(true);
  }, [pesquisa]);

  const opcoes = useMemo(
    () =>
      (op?.pedidos ?? []).flatMap((p) =>
        p.pesquisas.map((q) => ({
          valor: `${p.id}|${q.indice}`,
          rotulo: `Pedido de ${diaAno(p.recebidoEm)} · pesquisa ${q.indice + 1} · ${NIVEIS[q.nivel].longo}${p.doContratoDoCaso ? ' · do contrato deste caso' : ''}`,
        })),
      ),
    [op],
  );

  if (erro) return <AvisoSimples titulo="Trazer do pedido" texto={erro} aoFechar={aoFechar} />;
  if (!op) {
    return (
      <div className="cortina cad-cortina">
        <div className="dialogo">Abrindo os pedidos…</div>
      </div>
    );
  }

  const querDivida = divida && !!pesquisa && pesquisa.marcosQueFaltam.length > 0;
  const querNotas = !!(notas && pesquisa?.notas && !pesquisa.notasJaNoCaso && pesquisa.notasCabem);
  const total = marcados.size + (querDivida ? 1 : 0) + (querNotas ? 1 : 0);

  async function trazer() {
    if (!escolha || total === 0) return;
    setTrazendo(true);
    try {
      const r = await apiFormulario.trazer(casoId, { pedidoId: escolha.pedidoId, pesquisa: escolha.pesquisa, investigados: [...marcados], divida: querDivida, notas: querNotas });
      const partes = [
        r.investigados ? plural(r.investigados, 'investigado', 'investigados') : '',
        r.marcos.length ? plural(r.marcos.length, 'marco da dívida', 'marcos da dívida') : '',
        r.notas ? 'o que o cliente informou' : '',
      ].filter(Boolean);
      avisar(partes.length ? `Trazido do pedido: ${partes.join(', ')}.` : 'Nada a trazer: o caso já tinha tudo.');
      aoTrazido();
    } catch (e) {
      avisarErro(e);
      setTrazendo(false);
    }
  }

  const alternar = (i: number) =>
    setMarcados((m) => {
      const n = new Set(m);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="dialogo cad-dialogo cad-largo" role="dialog" aria-modal="true" aria-label="Trazer do pedido">
        <h2 className="titulo-secao">Trazer do pedido</h2>
        <div className="cad-dialogo-corpo imv">
          {op.pedidos.length === 0 ? (
            <p className="cad-nada">Este cliente não tem pedido de pesquisa para trazer.</p>
          ) : (
            <>
              <p className="cad-sub" style={{ margin: 0 }}>
                Caso <b className="dado">{casoCodigo}</b> · <b>Só completa o que está vazio</b>: nada do que o caso já tem é trocado.
              </p>
              <label className="ped-escolha-pedido">
                <span className="imv-rotulo">De qual pedido</span>
                <span className="imv-in">
                  <select
                    value={escolha ? `${escolha.pedidoId}|${escolha.pesquisa}` : ''}
                    onChange={(e) => {
                      const [pedidoId, n] = e.target.value.split('|');
                      setEscolha({ pedidoId: pedidoId!, pesquisa: Number(n) });
                    }}
                  >
                    {opcoes.map((o) => (
                      <option key={o.valor} value={o.valor}>
                        {o.rotulo}
                      </option>
                    ))}
                  </select>
                </span>
              </label>

              {pesquisa && (
                <>
                  <div className="ped-trazer">
                    <span className="imv-rotulo">Investigados</span>
                    {pesquisa.investigados.map((x) => (
                      <label key={x.indice} className={`ped-trazer-item${x.jaNoCaso ? ' fora' : ''}`}>
                        <input type="checkbox" disabled={x.jaNoCaso} checked={!x.jaNoCaso && marcados.has(x.indice)} onChange={() => alternar(x.indice)} />
                        <span>
                          <b>{x.nome}</b> {x.documento && <span className="dado">{x.documento}</span>}
                          <br />
                          <small>{QUALIFICACOES[x.papel]}</small>
                          {x.jaNoCaso && <small className="ped-ja">já está no caso — fica como está</small>}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="ped-trazer">
                    <span className="imv-rotulo">Sobre a dívida</span>
                    {pesquisa.marcosQueFaltam.length > 0 ? (
                      <label className="ped-trazer-item">
                        <input type="checkbox" checked={divida} onChange={(e) => setDivida(e.target.checked)} />
                        <span>
                          <b>{pesquisa.marcosQueFaltam.map((m) => m.rotulo).join(', ')}</b>
                          <br />
                          <small>
                            {pesquisa.marcosQueFaltam
                              .map((m) => (m.campo === 'dividaValor' ? paraReais(m.valor) : m.campo === 'processoNumero' ? formatarProcesso(m.valor) : paraTela(m.valor)))
                              .join(' · ')}{' '}
                            — o caso está em branco {pesquisa.marcosQueFaltam.length === 1 ? 'neste' : 'nestes'}
                          </small>
                        </span>
                      </label>
                    ) : (
                      <p className="ped-nada">Nada a trazer: o pedido não traz marco que o caso ainda não tenha.</p>
                    )}
                    {pesquisa.marcosQueOCasoJaTem.length > 0 && (
                      <label className="ped-trazer-item fora">
                        <input type="checkbox" disabled checked={false} readOnly />
                        <span>
                          <b>{pesquisa.marcosQueOCasoJaTem.map((m) => m.rotulo).join(', ')}</b>
                          <small className="ped-ja">o caso já tem — fica como está</small>
                        </span>
                      </label>
                    )}
                  </div>

                  {pesquisa.notas && (
                    <div className="ped-trazer">
                      <span className="imv-rotulo">O que o cliente informou</span>
                      <label className={`ped-trazer-item${pesquisa.notasJaNoCaso || !pesquisa.notasCabem ? ' fora' : ''}`}>
                        <input
                          type="checkbox"
                          disabled={pesquisa.notasJaNoCaso || !pesquisa.notasCabem}
                          checked={!pesquisa.notasJaNoCaso && pesquisa.notasCabem && notas}
                          onChange={(e) => setNotas(e.target.checked)}
                        />
                        <span>
                          <b>Na observação do caso, no fim</b>
                          <small className="ped-notas">{pesquisa.notas}</small>
                          {pesquisa.notasJaNoCaso ? (
                            <small className="ped-ja">já está na observação do caso</small>
                          ) : !pesquisa.notasCabem ? (
                            <small className="ped-ja">não cabe na observação do caso (4.000 caracteres) — leia no pedido e resuma à mão</small>
                          ) : (
                            <small>A observação do caso fica só no Hub. Não vai para a ficha do investigado, que sai impressa no relatório.</small>
                          )}
                        </span>
                      </label>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
        <div className="dialogo-acoes">
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          {op.pedidos.length > 0 && (
            <button type="button" className="botao-primario" disabled={trazendo || total === 0} onClick={() => void trazer()}>
              <Icone nome="trazer" tamanho={15} /> {trazendo ? 'Trazendo…' : total === 0 ? 'Nada a trazer' : `Trazer ${plural(total, 'item', 'itens')}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
